<template>
    <f7-page class="chat-withdrawal-view" ref="self">
        <f7-navbar
            ref="navbar"
            class="chat-withdrawal-view__navbar"
            :class="{'with-subnavbar': (withdrawal && P_WITHDRAWAL_STATUS.ONAPPEAL === withdrawal!.status)}"
        >
            <WithdrawalChatViewNavbarContent
                ref="navbarContent"
                :chat-resource="chatResource"
                :withdrawal="withdrawal"
                @closePage="closePage"
                @subnavbarEventClick="subnavbarEventClick"
            />
        </f7-navbar>

        <f7-messagebar
            v-if="!chatInfoIsLoading"
            @messagebar:focus="messagebarFocus"
            @messagebar:blur="messagebarBlur"
            ref="messagebar"
            v-model:value="messageText"
            :attachments-visible="attachmentsVisible"
            :sheet-visible="false"
        >
            <template #inner-start>
                <div class="attach-file-button-wrapper" @click.stop="tryUploadImage"
                     :class="{'disabled' : attachments.length > 0}">
                    <i class="icon svg-clip"></i>
                </div>
                <input accept="image/*,application/pdf" type="file" ref="input-file" class="input-file"
                       @change="inputFileEventChange">
            </template>
            <template #inner-end>
                <f7-preloader color="#007AFF" class="preloader" v-if="sendMessageRequest"></f7-preloader>
                <i v-else class="send-icon icon i-svg-arrow-top"
                   :class="{'active': sendActive, 'disabled': !sendActive}"
                   @click="sendMessage" @touchstart="sendMessage"></i>
            </template>
            <f7-messagebar-attachments>
                <f7-messagebar-attachment
                    v-for="(image, index) in vc.filterAttachmentsByTypes()"
                    :key="index"
                    :image="image.path"
                    @attachment:delete="vc.removeAttachment"
                ></f7-messagebar-attachment>
            </f7-messagebar-attachments>
        </f7-messagebar>

        <TransitionGroup name="fade" mode="out-in">
            <template v-if="!chatInfoIsLoading && chatResource">
                <MessagesContent
                    :chat="chatResource"
                    @imageClick="openImage"
                />
            </template>
            <template v-else>
                <div>
                    <f7-preloader :size="28"></f7-preloader>
                </div>
            </template>
        </TransitionGroup>
        <f7-photo-browser ref="photo-browser" :photos="photos" :thumbs="thumbs" type="popup"/>
    </f7-page>
</template>

<script lang="ts">
import Helper from "@/helpers/helper";
import {f7} from "framework7-vue";
import NoMessages from './components/NoMessages.vue';
import {defineComponent, ref} from 'vue';
import dayjs from "dayjs";
import 'dayjs/locale/ru'
import isBetween from 'dayjs/plugin/isBetween'
import ChatDataController from '@/controllers/ChatDataController';
import ChatViewController from '@/controllers/chat-view/ChatViewController';
import {P_WITHDRAWAL_STATUS} from "@/entities/enums/PWithdrawalStatus";
import WithdrawalService from "@/services/operations/withdrawal/WithdrawalService";
//@ts-ignore
import AppController from "@target/components/App/ts/AppController";
import WithdrawalChatViewNavbarContent from "@/views/chat-withdrawal/components/WithdrawalChatViewNavbarContent.vue";
import {BuyingOperationStatus} from "@enums/BuyingOperationStatus";
import MessagesContent from "@/views/chat/components/MessagesContent.vue";
import {getDevice} from "framework7/shared/get-device";
import ServiceChat from "@/services/v2/data/service-chat/ServiceChat";
import ServiceTelegram from "@/services/v2/service-telegram/ServiceTelegram";
import {useTranslate} from "@tolgee/vue";

dayjs.extend(isBetween)

export default defineComponent({
    components: {
        MessagesContent,
        WithdrawalChatViewNavbarContent,
        NoMessages,
    },
    setup() {
        const {t} = useTranslate();
        const withdrawalService: WithdrawalService = AppController.getInstance().withdrawalService;
        const serviceTelegram: ServiceTelegram | null = AppController.getInstance()?.service<ServiceTelegram>('telegram');
        let {withdrawal} = withdrawalService;
        const myName = "";
        const sendMessageRequest = ref(false)
        const messageText = ref('')
        const height = window.innerHeight
        const messagebar = null;
        const disableUploadFile = ref(false);
        const chatController = ChatDataController.getInstance();
        const chatResource = ServiceChat.of().chat;
        const vc = ChatViewController.getInstance();
        const attachments = vc.attachments;
        const observer = new MutationObserver(() => {
            const pce: HTMLElement = document.querySelector('.chat-withdrawal-view')!.querySelector('.page-content')!;
            pce.scrollTo({top: pce.scrollHeight, behavior: 'smooth'});
        });
        const loadingBtn = ref(false);
        const placeholder = t.value('chat.input-placeholder');

        return {
            dayjs,
            withdrawalService,
            serviceTelegram,
            placeholder,
            Helper,
            photos: [],
            thumbs: [],
            loadingBtn,
            observer,
            attachments,
            vc,
            t,
            disableUploadFile,
            messagebar,
            height,
            messageText,
            myName,
            withdrawal,
            sendMessageRequest,
            chatController,
            chatResource
        }
    },
    data() {
        return {
            chatInfoIsLoading: false,
            messagesData: [],
            images: [],
        };
    },
    watch: {
        'withdrawal'(value) {
            if (value.status &&
                [
                    BuyingOperationStatus.CASHED,
                    BuyingOperationStatus.ONAPPEAL,
                ].includes(value.status)) {
                this.showSubnavbar();
            } else {
                this.hideSubnavbar();
            }
        }
    },
    computed: {
        //TODO useless computed property(Нужно в будущем переделать весь компонент на setup)
        P_WITHDRAWAL_STATUS() {
            return P_WITHDRAWAL_STATUS
        },
        attachmentsVisible() {
            return this.vc.filterAttachmentsByTypes().length > 0;
        },
        sendActive() {
            return this.messageText.length > 0 || this.attachments.length > 0;
        }
    },
    async created() {
        await this.fetchChatData();
        setTimeout(() => {
            if (this.checkShowSubnavbar()) this.showSubnavbar();
        }, 1000);
    },
    async mounted() {
        const self = this;
        this.setPopupHeight('up');
    },
    unmounted() {
        this.observer.disconnect();
    },
    methods: {
        tryUploadImage() {
            if (getDevice().ios && getDevice().cordova) {
                try {
                    cordova.plugins.diagnostic.getCameraRollAuthorizationStatus((status: any) => {
                        switch (status) {
                            case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
                                cordova.plugins.diagnostic.requestCameraRollAuthorization(() => {
                                    switch (status) {
                                        default:
                                            cordova.plugins.diagnostic.getCameraAuthorizationStatus((status) => {
                                                switch (status) {
                                                    case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
                                                        cordova.plugins.diagnostic.requestCameraAuthorization((status) => {
                                                            (this.$refs as any)['input-file'].click()
                                                        });
                                                        break;
                                                    //case cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS:
                                                    //    cordova.plugins.diagnostic.switchToSettings();
                                                    //        break;
                                                    default:
                                                        (this.$refs as any)['input-file'].click()
                                                }
                                            });
                                    }
                                });
                                break;
                            //case cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS:
                            //cordova.plugins.diagnostic.switchToSettings();
                            //break;
                            default:
                                cordova.plugins.diagnostic.getCameraAuthorizationStatus((status) => {
                                    switch (status) {
                                        case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
                                            cordova.plugins.diagnostic.requestCameraAuthorization((status) => {
                                                // @ts-ignore
                                                (this.$refs as any)['input-file'].click()
                                            });
                                            break;
                                        //case cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS:
                                        //    cordova.plugins.diagnostic.switchToSettings();
                                        //        break;
                                        default:
                                            // @ts-ignore
                                            (this.$refs as any)['input-file'].click()
                                    }
                                });
                        }
                    });
                } catch (e) {
                    console.log(e);
                }
                return;
            } else {
                (this.$refs as any)['input-file'].click()
            }
        },
        checkShowSubnavbar() {
            return !!(this.withdrawal &&
                this.withdrawal.status && [
                    P_WITHDRAWAL_STATUS.CASHED,
                    P_WITHDRAWAL_STATUS.ONAPPEAL,
                ].includes(this.withdrawal.status));
        },
        showSubnavbar() {
            const navbarDOM = ((this.$refs.navbar as any).$el) as HTMLElement;
            navbarDOM.classList.add('with-custom-subnavbar');
            const pageContentDOM = (((this.$refs.self as any).$el) as HTMLElement).querySelector('.page-content') as HTMLElement;
            pageContentDOM.style.paddingTop = `88px`;
            (this.$refs.navbarContent as any).showSubnavbar = true;
        },
        hideSubnavbar() {
            const navbarDOM = ((this.$refs.navbar as any).$el) as HTMLElement;
            navbarDOM.classList.add('with-custom-subnavbar');
            const pageContentDOM = (((this.$refs.self as any).$el) as HTMLElement).querySelector('.page-content') as HTMLElement;
            pageContentDOM.style.paddingTop = `44px`;
            (this.$refs.navbarContent as any).showSubnavbar = false;
        },
        subnavbarEventClick() {
            if (this.withdrawal!.status === P_WITHDRAWAL_STATUS.ONAPPEAL) {
                this.openToFinishAppealAlert();
            } else {
                this.openToAppealAlert();
            }
        },
        openToFinishAppealAlert() {
            const self = this;
            f7.dialog.create({
                title: this.$t("alerts.onappeal-finish.title"),
                text: this.$t("alerts.onappeal-finish.text"),
                cssClass: "dialog_complain",
                buttons: [
                    {
                        text: this.$t("alerts.onappeal-finish.no")
                    },
                    {
                        text: this.$t("alerts.onappeal-finish.yes"),
                        onClick: async () => {
                            await self.makeRequestToFinishAppeal();
                            this.hideSubnavbar();
                        }
                    }
                ]
            }).open();
        },
        openToAppealAlert() {
            const self = this;
            f7.dialog.create({
                title: this.$t("alerts.complain.title"),
                text: this.$t("alerts.complain.text"),
                cssClass: "dialog_complain",
                buttons: [
                    {
                        text: this.$t("alerts.complain.no")
                    },
                    {
                        text: this.$t("alerts.complain.yes"),
                        onClick: async () => {
                            await self.makeRequestToAppeal();
                            this.hideSubnavbar();
                        }
                    }
                ]
            }).open();
        },
        async makeRequestToFinishAppeal() {
            try {
                f7.preloader.show();
                await this.withdrawalService.confirmWithdrawal();
            } catch (e: any) {
                f7.dialog.alert(this.t('g.errors.alert.default-text'), this.t('g.errors.alert.default-title'));
            } finally {
                f7.preloader.hide();
            }
        },
        async makeRequestToAppeal() {
            try {
                f7.preloader.show();
                await this.withdrawalService.toAppealWithdrawal();
            } catch (e: any) {
                f7.dialog.alert(this.t('g.errors.alert.default-text'), this.t('g.errors.alert.default-title'));
            } finally {
                f7.preloader.hide();
            }
        },
        openImage(message: any) {
            f7.photoBrowser.create({
                photos: [message.image],
                theme: 'dark'
            }).open();
        },
        uploadFile() {
            console.log("uploadFile");
        },
        setPopupHeight(arrow: string) {
            const popup = this.$refs['self'].$el.closest('.popup-router-open-in');
            if (arrow === 'down') {
                popup.style.height = '50%';
                popup.style.top = 'unset';
                popup.style.bottom = '0';
            } else {
                popup.style.height = '100%';
                popup.style.top = 'unset';
                popup.style.bottom = '0';
            }
        },
        async fetchChatData() {
            try {
                this.chatInfoIsLoading = true;
                const serverModelChat = await ServiceChat.of().fetchChatByMsid(this.withdrawal!.msid!);
                const modelChatF7 = ServiceChat.of().modelChatF7Factory(serverModelChat);
                ServiceChat.of().chat.value = modelChatF7;
                ChatDataController.getInstance().resetPaymentChatCount(this.withdrawal.id);
                this.myName = this.chatResource!.client.name;

                const pce: HTMLElement = document.querySelector('.chat-withdrawal-view')!.querySelector('.page-content')!;
                this.observer.observe(pce, {childList: true});
            } catch (e) {
                console.error(e);
                f7.dialog.alert(this.t("chat.alert.first"), this.t("chat.alert.second"), () => this.closePage());
            } finally {
                setTimeout(() => {
                    this.chatInfoIsLoading = false;
                }, 700);
            }
        },
        convertDatetime(dateString) {
            const date = new Date(dateString);
            const day = date.getDate();
            const dayString = day < 10 ? `0${day}` : day.toString();
            const month = date.getMonth();
            const monthString = month < 10 ? `0${month}` : month.toString();
            const year = date.getFullYear();
            const hour = date.getHours();
            const hourString = hour < 10 ? `0${hour}` : hour.toString();
            const minutes = date.getMinutes();
            const minutesString = minutes < 10 ? `0${minutes}` : minutes.toString();
            return `${dayString}.${monthString}.${year} ${hourString}:${minutesString}`;
        },
        async closePage() {
            await this.closeKeyboardFocusInput(() => {
                try {
                  // @ts-ignore
                  f7.popup.get(this.$refs['self'].$el.closest('.popup-router-open-in')).close()
                } catch (e) {
                }
            });
        },
        async closeKeyboardFocusInput(callback: Function) {
            if (getDevice().cordova) {
                // @ts-ignore
                this.$refs.self.$el.querySelectorAll('input, textarea').forEach(element => {
                    element.blur();
                });
                setTimeout(async () => {
                    await callback();
                }, 314);
                return false;
            }
            callback();
        },
        messagebarBlur() {
            document.documentElement.classList.remove('device-with-keyboard');
            if (!getDevice().cordova && !this.serviceTelegram?.isTelegram) {
                const el = this.$refs['messagebar'].$el;
                el.style.paddingBottom = `${parseInt(getComputedStyle(document.documentElement).getPropertyValue('--f7-safe-area-bottom'))}px`;

                const fullHeight = document.documentElement.clientHeight;
                const currentHeight = window.innerHeight;

                document.body.style.height = `calc(100%)`;
                document.body.style.transform = `translateY(${0}px)`;
            }
            const pce = document.querySelector('.chat-withdrawal-view')!.querySelector('.page-content')!;
            pce.scrollTop = pce.scrollHeight;

            const el = this.$refs['messagebar'].$el;
            el.style.paddingBottom = 'calc(0px + var(--f7-safe-area-bottom))';
        },
        messagebarFocus() {
            document.documentElement.classList.add('device-with-keyboard');

            if (!getDevice().cordova && !this.serviceTelegram?.isTelegram) {
                setTimeout(() => {
                    const fullHeight2 = document.documentElement.clientHeight;
                    const currentHeight2 = window.innerHeight;
                    document.body.style.height = `${currentHeight2}px`;
                    document.body.style.transform = `translateY(${fullHeight2 - currentHeight2}px)`;
                }, 130);
            }
            setTimeout(() => {
                const pce = document.querySelector('.chat-withdrawal-view')!.querySelector('.page-content')!;
                pce.scrollTo({
                    top: pce.scrollHeight,
                    behavior: "smooth"
                });
                // }
            }, 401);
            const el = this.$refs['messagebar'].$el;
            el.style.paddingBottom = 'calc(0px)';
        },
        async sendMessage() {
            const text = this.messageText.replace(/\n/g, '<br>').trim();

            if (text.length === 0 && this.attachments.length === 0) return false;

            this.sendMessageRequest = true;

            try {
                const responseMessage = await this.vc.sendTextMessage({
                    msid: this.withdrawal!.msid!,
                    message: text,
                    wait_files: this.attachments.length
                });

                if (this.attachments.length > 0) {
                    await Promise.all(
                        this.attachments.map(async (attachment) => {
                            const responseMessageFile = await this.vc.sendFileMessage({
                                chatId: this.chatResource!.id,
                                messageId: responseMessage.id,
                                file: attachment.file
                            });
                            responseMessage.attachments.push(responseMessageFile);
                        })
                    );
                }

                this.chatController.appendMessage(responseMessage);

                this.vc.removeAttachment();
                this.messageText = '';

                const pageContent = document.querySelector('.chat-withdrawal-view .page-content');
                if (pageContent) {
                    setTimeout(() => {
                        pageContent.scrollTo({
                            top: pageContent.scrollHeight,
                            behavior: 'smooth'
                        });
                    }, 600);
                }
            } catch (e: any) {
                if (typeof e.type !== 'undefined') {
                    switch (e.type) {
                        case 'ServerAuthError':
                            this.closePage();
                    }
                }
            } finally {
                this.sendMessageRequest = false;
            }
        },
        inputFileEventChange(event: Event) {
            this.vc.uploadFile(event);
            if (this.vc.fileIsPDF(this.vc.attachments.value[0].file)) {
                this.sendMessage();
            }
            (event.target as HTMLInputElement).value = "";
        }
    },
});
</script>

<style lang="scss">
.chat-withdrawal-view__navbar {
    --f7-subnavbar-height: 32px;

    .online-status {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        @include text(12px, 400, #818E95, 14.4px);

        i {
            width: 6px;
            height: 6px;
            border-radius: 50%;

            &.green {
                background: #32BF61;
            }

            &.red {
                background: #818E95;
            }
        }
    }

    .right {
        margin-left: 0 !important;
        min-width: 100px;
        justify-content: flex-end;

        .navbar-withdrawal-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
            align-items: flex-end;
            margin-right: 8px;

            &__id {
                @include text(12px, 500, var(--brown1), 14.4px);
            }

            &__status {
                @include text(12px, 500, var(--blue1), 14.4px);
                background: none !important;
            }
        }
    }

    &::after {
        content: '';
        position: absolute;
        background-color: var(--f7-navbar-border-color, var(--f7-bars-border-color));
        display: block;
        z-index: 15;
        top: auto;
        right: auto;
        bottom: 0;
        left: 0;
        height: 1px;
        width: 100%;
        transform-origin: 50% 100%;
        transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
    }

    .subnavbar {
        cursor: pointer;

        .subnavbar-inner {
            justify-content: center;
            width: 100%;
            @include text(12px, 500, var(--grey2), 14.4px);

            &.active-state {
                background-color: #00000026;
            }
        }
    }
}

.chat-withdrawal-view {
    --f7-subnavbar-height: 32px;
    --f7-page-content-extra-padding-bottom: 0px;

    .toolbar {
        .toolbar-inner {
            .messagebar-attachment {
                img {
                    object-fit: contain;
                    max-height: 155px;
                    max-width: 100%;
                }

                @media (min-width: 426px) {
                    max-height: 120px !important;
                }
            }

            .attach-file-button-wrapper {
                z-index: 99999;
                display: flex;
                align-self: flex-end;
                cursor: pointer;
                margin-right: 4px;
                margin-bottom: 8px;

                .svg-clip {
                }
            }

            .input-file {
                display: none;
            }

            .send-icon {
                align-self: flex-end;
                margin-bottom: 5px;
            }

            .preloader {
                align-self: flex-end;
                margin-bottom: 10px;
            }
        }
    }


    .page-content {
        background-color: #FFF;
        transform: translate3d(0, 0, 0);
        will-change: transform;
        transition: 0.5s padding-top cubic-bezier(0.250, 0.460, 0.450, 0.940);

        &::-webkit-scrollbar {
            display: none;
        }

        .preloader {
            margin-top: 44px;
            margin-left: 50%;
            transform: translateX(-50%);
        }

        .fade-enter-active,
        .fade-leave-active {
            transition: opacity 0.5s ease;
        }

        .fade-enter-from,
        .fade-leave-to {
            opacity: 0;
        }
    }

    .messages-block {
        // height: 100%;
        // overflow: scroll;

        .appeal-title {
            margin: 16px;
            text-align: center;
            @include text(12px, 500, #818E95, 14.4px);
        }

        &::-webkit-scrollbar {
            display: none;
        }

        .no-messages {
            padding-top: 56px;
        }

        .messages-title:first-child {
            margin-top: 16px;
        }

        .message + .messages-title {
            margin-top: 24px;
        }

        .message {
            .message-image {
            }

            cursor: pointer;

            .avatar {
                width: 29px;
                height: 29px;
                //background-size: contain;
                &.agent {
                    background-image: url("data:image/svg+xml,%3Csvg width='29' height='29' viewBox='0 0 29 29' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_4080_140367)'%3E%3Crect width='29' height='29' rx='14.5' fill='%23EFEFF4'/%3E%3Cpath d='M6.60136 11.7097L6.53674 11.6704C5.65105 13.1246 5.17973 14.7861 5.17239 16.481V16.4813C5.17239 18.4439 6.10998 20.091 7.87533 21.2415C9.55147 22.3344 11.924 22.9375 14.5552 22.9355C17.1861 22.9335 19.5588 22.3341 21.235 21.2415C23.0003 20.0912 23.9383 18.4441 23.9379 16.4814C23.9379 14.247 23.0795 12.0075 21.5826 10.3352C20.371 8.98126 18.1682 7.36866 14.5552 7.36584L14.5551 7.36584C12.7154 7.36869 11.2404 7.78726 10.0735 8.37775L10.0735 8.37765L10.07 8.37965L10.0565 8.38739C8.6185 9.11897 7.40459 10.2498 6.54561 11.6577L6.54551 11.6578L6.54535 11.658C6.54386 11.6599 6.53959 11.6653 6.53563 11.6722L6.60136 11.7097ZM6.60136 11.7097C6.60247 11.7078 6.60385 11.706 6.60525 11.7043C6.60636 11.7029 6.60749 11.7014 6.6085 11.6999L7.91666 21.1781C6.17077 20.0403 5.24805 18.416 5.24805 16.4813C5.25534 14.8003 5.7228 13.1522 6.60136 11.7097Z' fill='white' stroke='%23423636' stroke-width='0.151323'/%3E%3Cpath d='M14.5751 6.57988H14.5749L14.5349 6.57988L14.5347 6.57988C12.7489 6.58488 11.2789 6.95552 10.0829 7.49764C9.44458 6.61405 8.48859 6.0559 7.54454 6.0559C6.75158 6.0559 5.95503 6.46486 5.3558 7.16807L5.35578 7.16809C4.79333 7.82862 4.46856 8.65719 4.46856 9.44317C4.46856 10.2858 4.90221 11.0807 5.63484 11.6422C4.81373 13.1277 4.37977 14.7899 4.37209 16.4808V16.4812C4.37209 18.7195 5.43315 20.5933 7.43261 21.8968L7.43262 21.8968C9.23421 23.071 11.7566 23.7169 14.5352 23.7217H14.5354L14.5754 23.7217L14.5755 23.7217C17.3544 23.7167 19.8763 23.071 21.6781 21.8968L21.6368 21.8334L21.6781 21.8968C23.6776 20.5933 24.739 18.7197 24.739 16.4812L24.739 16.4808C24.7312 14.7957 24.3001 13.1389 23.4843 11.6573C24.2315 11.0965 24.6711 10.2937 24.6711 9.44317C24.6711 8.65719 24.3463 7.82862 23.7839 7.16809C23.185 6.46486 22.3885 6.0559 21.5952 6.0559C20.6476 6.0559 19.6883 6.61836 19.0489 7.5073C17.8489 6.95973 16.3719 6.58337 14.5751 6.57988ZM7.95829 21.1149L7.95827 21.1149C6.23189 19.9898 5.32406 18.3884 5.32401 16.4816C5.33127 14.8151 5.79453 13.1812 6.66531 11.7508L6.6671 11.7485C6.66841 11.7468 6.67023 11.7443 6.6722 11.7413L6.67223 11.7413L6.67341 11.7394C7.51857 10.3531 8.71301 9.24055 10.1271 8.52154L10.1272 8.52162L10.1305 8.5197L10.1439 8.51199C11.2895 7.93261 12.7406 7.52012 14.5555 7.51729C18.117 7.52011 20.2811 9.10749 21.4702 10.4362L21.5265 10.3858L21.4702 10.4362C22.9423 12.081 23.7868 14.2846 23.7868 16.4815C23.7873 18.3886 22.8791 19.9899 21.1527 21.1149L21.194 21.1782L21.1527 21.1149C19.5077 22.187 17.1662 22.7823 14.5554 22.7843C11.9446 22.7863 9.60323 22.1875 7.95829 21.1149ZM6.60555 11.7044C6.60666 11.703 6.60779 11.7016 6.6088 11.7L5.24835 16.4814C5.25564 14.8004 5.7231 13.1524 6.60166 11.7098C6.60277 11.7079 6.60415 11.7062 6.60555 11.7044Z' fill='%23423636' stroke='%23423636' stroke-width='0.151323'/%3E%3Cpath d='M15.9669 16.3838C15.9669 16.846 14.6348 17.4902 14.6348 17.4902C14.6348 17.4902 13.3027 16.846 13.3027 16.3838C13.3027 15.9215 13.8994 15.8661 14.6348 15.8661C15.3702 15.8661 15.9669 15.9216 15.9669 16.3838Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M13.6326 16.324C13.6249 16.3321 13.6149 16.343 13.6149 16.3837C13.6149 16.381 13.6149 16.3796 13.6152 16.3795C13.6169 16.3792 13.63 16.4288 13.7321 16.5313C13.835 16.6347 13.9812 16.7449 14.1402 16.8494C14.297 16.9524 14.4553 17.0428 14.5754 17.1078C14.5965 17.1192 14.6165 17.1299 14.6349 17.1396C14.6533 17.1299 14.6732 17.1192 14.6944 17.1078C14.8145 17.0428 14.9728 16.9524 15.1295 16.8494C15.2886 16.7449 15.4348 16.6347 15.5377 16.5313C15.6398 16.4288 15.6529 16.3792 15.6546 16.3795C15.6549 16.3796 15.6549 16.381 15.6549 16.3837C15.6549 16.343 15.6449 16.3321 15.6372 16.324C15.6213 16.3075 15.5804 16.2782 15.4878 16.2507C15.2963 16.1938 15.0069 16.1781 14.6349 16.1781C14.2629 16.1781 13.9735 16.1938 13.782 16.2507C13.6893 16.2782 13.6485 16.3075 13.6326 16.324ZM14.6349 17.4901L14.4989 17.771L14.4974 17.7703L14.4939 17.7686L14.4814 17.7625C14.4708 17.7572 14.4555 17.7495 14.4362 17.7397C14.3977 17.7202 14.3433 17.692 14.2782 17.6567C14.1485 17.5865 13.9738 17.4869 13.7975 17.3711C13.6235 17.2567 13.4367 17.1193 13.2898 16.9718C13.1588 16.8402 12.9907 16.6341 12.9907 16.3837C12.9907 16.1932 13.0554 16.0239 13.1825 15.8916C13.3015 15.7677 13.4557 15.6965 13.6042 15.6523C13.8948 15.566 14.2715 15.5539 14.6349 15.5539C14.9983 15.5539 15.375 15.566 15.6656 15.6524C15.8141 15.6965 15.9683 15.7677 16.0873 15.8916C16.2144 16.0239 16.2791 16.1933 16.2791 16.3837C16.2791 16.6341 16.111 16.8402 15.98 16.9718C15.8331 17.1193 15.6463 17.2567 15.4723 17.3711C15.296 17.4869 15.1213 17.5865 14.9916 17.6567C14.9265 17.692 14.872 17.7202 14.8336 17.7397C14.8143 17.7495 14.799 17.7572 14.7883 17.7625L14.7759 17.7686L14.7724 17.7703L14.7713 17.7708L14.6349 17.4901ZM14.6349 17.4901L14.7713 17.7708C14.6855 17.8123 14.5848 17.8125 14.4989 17.771L14.6349 17.4901Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M14.6372 17.0602C14.8769 17.0602 15.0711 17.2544 15.0711 17.4941V18.0443C15.0711 18.1357 15.0423 18.2247 14.9888 18.2987L14.6372 18.0443C14.9888 18.2987 14.9887 18.2989 14.9885 18.2991L14.9882 18.2995L14.9875 18.3005L14.9857 18.3029L14.9809 18.3094L14.9658 18.329C14.9534 18.345 14.9362 18.3664 14.9143 18.3924C14.8704 18.4443 14.8073 18.5145 14.7253 18.5946C14.5621 18.7542 14.3198 18.9571 14.0017 19.1313C13.3536 19.4863 12.4084 19.7098 11.2365 19.2466C11.0136 19.1585 10.9043 18.9064 10.9924 18.6835C11.0805 18.4606 11.3326 18.3514 11.5555 18.4395C12.4547 18.7949 13.1302 18.6191 13.5848 18.3701C13.8182 18.2423 13.9979 18.092 14.1187 17.9739C14.1516 17.9418 14.1798 17.9123 14.2033 17.8868V17.4941C14.2033 17.2544 14.3975 17.0602 14.6372 17.0602Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M14.6332 17.0602C14.8728 17.0602 15.0671 17.2544 15.0671 17.4941V17.8868C15.0905 17.9123 15.1188 17.9418 15.1516 17.9739C15.2725 18.092 15.4522 18.2423 15.6855 18.3701C16.1402 18.6191 16.8156 18.7949 17.7149 18.4395C17.9378 18.3514 18.1899 18.4606 18.278 18.6835C18.366 18.9064 18.2568 19.1585 18.0339 19.2466C16.8619 19.7098 15.9167 19.4863 15.2686 19.1313C14.9505 18.9571 14.7083 18.7542 14.545 18.5946C14.4631 18.5145 14.3999 18.4443 14.3561 18.3924C14.3341 18.3664 14.3169 18.345 14.3045 18.329L14.2895 18.3094L14.2846 18.3029L14.2829 18.3005L14.2822 18.2995L14.2819 18.2991C14.2817 18.2989 14.2816 18.2987 14.6332 18.0443L14.2816 18.2987C14.228 18.2247 14.1992 18.1357 14.1992 18.0443V17.4941C14.1992 17.2544 14.3935 17.0602 14.6332 17.0602Z' fill='%23423636'/%3E%3Cpath d='M21.5986 11.8158C21.5868 11.7081 21.53 11.61 21.442 11.5446C21.354 11.4793 21.2424 11.4525 21.1336 11.4707L15.0841 12.4832H14.1747L8.12487 11.4707C8.01612 11.4525 7.90451 11.4793 7.81648 11.5446C7.72859 11.61 7.67186 11.7081 7.66 11.8158C7.65042 11.9053 7.43035 14.0198 8.50742 15.3254C8.97844 15.8965 9.61821 16.2259 10.4096 16.304C10.5895 16.3223 10.7702 16.3317 10.9511 16.332C11.8846 16.332 12.6648 16.0702 13.275 15.5514C13.6736 15.205 13.9868 14.7741 14.1909 14.2914H15.068C15.2721 14.7741 15.5851 15.205 15.9835 15.5514C16.5941 16.0702 17.3739 16.332 18.3078 16.332H18.308C18.4887 16.3316 18.6694 16.3223 18.8493 16.304C19.6404 16.2254 20.2806 15.8964 20.7515 15.3254C21.8287 14.0199 21.6086 11.9055 21.5986 11.8158Z' fill='%23423636'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_4080_140367'%3E%3Crect width='29' height='29' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
                }

                &.admin {
                    background-image: url("data:image/svg+xml,%3Csvg width='29' height='29' viewBox='0 0 29 29' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_4080_140366)'%3E%3Crect width='29' height='29' rx='14.5' fill='%23EFEFF4'/%3E%3Cpath d='M6.60136 11.7097L6.53674 11.6704C5.65105 13.1246 5.17973 14.7861 5.17239 16.481V16.4813C5.17239 18.4439 6.10998 20.091 7.87533 21.2415C9.55147 22.3344 11.924 22.9375 14.5552 22.9355C17.1861 22.9335 19.5588 22.3341 21.235 21.2415C23.0003 20.0912 23.9383 18.4441 23.9379 16.4814C23.9379 14.247 23.0795 12.0075 21.5826 10.3352C20.371 8.98126 18.1682 7.36866 14.5552 7.36584L14.5551 7.36584C12.7154 7.36869 11.2404 7.78726 10.0735 8.37775L10.0735 8.37765L10.07 8.37965L10.0565 8.38739C8.6185 9.11897 7.40459 10.2498 6.54561 11.6577L6.54551 11.6578L6.54535 11.658C6.54386 11.6599 6.53959 11.6653 6.53563 11.6722L6.60136 11.7097ZM6.60136 11.7097C6.60247 11.7078 6.60385 11.706 6.60525 11.7043C6.60636 11.7029 6.60749 11.7014 6.6085 11.6999L7.91666 21.1781C6.17077 20.0403 5.24805 18.416 5.24805 16.4813C5.25534 14.8003 5.7228 13.1522 6.60136 11.7097Z' fill='white' stroke='%23423636' stroke-width='0.151323'/%3E%3Cpath d='M14.5751 6.57988H14.5749L14.5349 6.57988L14.5347 6.57988C12.7489 6.58488 11.2789 6.95552 10.0829 7.49764C9.44458 6.61405 8.48859 6.0559 7.54454 6.0559C6.75158 6.0559 5.95503 6.46486 5.3558 7.16807L5.35578 7.16809C4.79333 7.82862 4.46856 8.65719 4.46856 9.44317C4.46856 10.2858 4.90221 11.0807 5.63484 11.6422C4.81373 13.1277 4.37977 14.7899 4.37209 16.4808V16.4812C4.37209 18.7195 5.43315 20.5933 7.43261 21.8968L7.43262 21.8968C9.23421 23.071 11.7566 23.7169 14.5352 23.7217H14.5354L14.5754 23.7217L14.5755 23.7217C17.3544 23.7167 19.8763 23.071 21.6781 21.8968L21.6368 21.8334L21.6781 21.8968C23.6776 20.5933 24.739 18.7197 24.739 16.4812L24.739 16.4808C24.7312 14.7957 24.3001 13.1389 23.4843 11.6573C24.2315 11.0965 24.6711 10.2937 24.6711 9.44317C24.6711 8.65719 24.3463 7.82862 23.7839 7.16809C23.185 6.46486 22.3885 6.0559 21.5952 6.0559C20.6476 6.0559 19.6883 6.61836 19.0489 7.5073C17.8489 6.95973 16.3719 6.58337 14.5751 6.57988ZM7.95829 21.1149L7.95827 21.1149C6.23189 19.9898 5.32406 18.3884 5.32401 16.4816C5.33127 14.8151 5.79453 13.1812 6.66531 11.7508L6.6671 11.7485C6.66841 11.7468 6.67023 11.7443 6.6722 11.7413L6.67223 11.7413L6.67341 11.7394C7.51857 10.3531 8.71301 9.24055 10.1271 8.52154L10.1272 8.52162L10.1305 8.5197L10.1439 8.51199C11.2895 7.93261 12.7406 7.52012 14.5555 7.51729C18.117 7.52011 20.2811 9.10749 21.4702 10.4362L21.5265 10.3858L21.4702 10.4362C22.9423 12.081 23.7868 14.2846 23.7868 16.4815C23.7873 18.3886 22.8791 19.9899 21.1527 21.1149L21.194 21.1782L21.1527 21.1149C19.5077 22.187 17.1662 22.7823 14.5554 22.7843C11.9446 22.7863 9.60323 22.1875 7.95829 21.1149ZM6.60555 11.7044C6.60666 11.703 6.60779 11.7016 6.6088 11.7L5.24835 16.4814C5.25564 14.8004 5.7231 13.1524 6.60166 11.7098C6.60277 11.7079 6.60415 11.7062 6.60555 11.7044Z' fill='%23423636' stroke='%23423636' stroke-width='0.151323'/%3E%3Cpath d='M15.9669 16.3838C15.9669 16.846 14.6348 17.4902 14.6348 17.4902C14.6348 17.4902 13.3027 16.846 13.3027 16.3838C13.3027 15.9215 13.8994 15.8661 14.6348 15.8661C15.3702 15.8661 15.9669 15.9216 15.9669 16.3838Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M13.6326 16.324C13.6249 16.3321 13.6149 16.343 13.6149 16.3837C13.6149 16.381 13.6149 16.3796 13.6152 16.3795C13.6169 16.3792 13.63 16.4288 13.7321 16.5313C13.835 16.6347 13.9812 16.7449 14.1402 16.8494C14.297 16.9524 14.4553 17.0428 14.5754 17.1078C14.5965 17.1192 14.6165 17.1299 14.6349 17.1396C14.6533 17.1299 14.6732 17.1192 14.6944 17.1078C14.8145 17.0428 14.9728 16.9524 15.1295 16.8494C15.2886 16.7449 15.4348 16.6347 15.5377 16.5313C15.6398 16.4288 15.6529 16.3792 15.6546 16.3795C15.6549 16.3796 15.6549 16.381 15.6549 16.3837C15.6549 16.343 15.6449 16.3321 15.6372 16.324C15.6213 16.3075 15.5804 16.2782 15.4878 16.2507C15.2963 16.1938 15.0069 16.1781 14.6349 16.1781C14.2629 16.1781 13.9735 16.1938 13.782 16.2507C13.6893 16.2782 13.6485 16.3075 13.6326 16.324ZM14.6349 17.4901L14.4989 17.771L14.4974 17.7703L14.4939 17.7686L14.4814 17.7625C14.4708 17.7572 14.4555 17.7495 14.4362 17.7397C14.3977 17.7202 14.3433 17.692 14.2782 17.6567C14.1485 17.5865 13.9738 17.4869 13.7975 17.3711C13.6235 17.2567 13.4367 17.1193 13.2898 16.9718C13.1588 16.8402 12.9907 16.6341 12.9907 16.3837C12.9907 16.1932 13.0554 16.0239 13.1825 15.8916C13.3015 15.7677 13.4557 15.6965 13.6042 15.6523C13.8948 15.566 14.2715 15.5539 14.6349 15.5539C14.9983 15.5539 15.375 15.566 15.6656 15.6524C15.8141 15.6965 15.9683 15.7677 16.0873 15.8916C16.2144 16.0239 16.2791 16.1933 16.2791 16.3837C16.2791 16.6341 16.111 16.8402 15.98 16.9718C15.8331 17.1193 15.6463 17.2567 15.4723 17.3711C15.296 17.4869 15.1213 17.5865 14.9916 17.6567C14.9265 17.692 14.872 17.7202 14.8336 17.7397C14.8143 17.7495 14.799 17.7572 14.7883 17.7625L14.7759 17.7686L14.7724 17.7703L14.7713 17.7708L14.6349 17.4901ZM14.6349 17.4901L14.7713 17.7708C14.6855 17.8123 14.5848 17.8125 14.4989 17.771L14.6349 17.4901Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M14.6372 17.0602C14.8769 17.0602 15.0711 17.2544 15.0711 17.4941V18.0443C15.0711 18.1357 15.0423 18.2247 14.9888 18.2987L14.6372 18.0443C14.9888 18.2987 14.9887 18.2989 14.9885 18.2991L14.9882 18.2995L14.9875 18.3005L14.9857 18.3029L14.9809 18.3094L14.9658 18.329C14.9534 18.345 14.9362 18.3664 14.9143 18.3924C14.8704 18.4443 14.8073 18.5145 14.7253 18.5946C14.5621 18.7542 14.3198 18.9571 14.0017 19.1313C13.3536 19.4863 12.4084 19.7098 11.2365 19.2466C11.0136 19.1585 10.9043 18.9064 10.9924 18.6835C11.0805 18.4606 11.3326 18.3514 11.5555 18.4395C12.4547 18.7949 13.1302 18.6191 13.5848 18.3701C13.8182 18.2423 13.9979 18.092 14.1187 17.9739C14.1516 17.9418 14.1798 17.9123 14.2033 17.8868V17.4941C14.2033 17.2544 14.3975 17.0602 14.6372 17.0602Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M14.6332 17.0602C14.8728 17.0602 15.0671 17.2544 15.0671 17.4941V17.8868C15.0905 17.9123 15.1188 17.9418 15.1516 17.9739C15.2725 18.092 15.4522 18.2423 15.6855 18.3701C16.1402 18.6191 16.8156 18.7949 17.7149 18.4395C17.9378 18.3514 18.1899 18.4606 18.278 18.6835C18.366 18.9064 18.2568 19.1585 18.0339 19.2466C16.8619 19.7098 15.9167 19.4863 15.2686 19.1313C14.9505 18.9571 14.7083 18.7542 14.545 18.5946C14.4631 18.5145 14.3999 18.4443 14.3561 18.3924C14.3341 18.3664 14.3169 18.345 14.3045 18.329L14.2895 18.3094L14.2846 18.3029L14.2829 18.3005L14.2822 18.2995L14.2819 18.2991C14.2817 18.2989 14.2816 18.2987 14.6332 18.0443L14.2816 18.2987C14.228 18.2247 14.1992 18.1357 14.1992 18.0443V17.4941C14.1992 17.2544 14.3935 17.0602 14.6332 17.0602Z' fill='%23423636'/%3E%3Cpath d='M21.5986 11.8158C21.5868 11.7081 21.53 11.61 21.442 11.5446C21.354 11.4793 21.2424 11.4525 21.1336 11.4707L15.0841 12.4832H14.1747L8.12487 11.4707C8.01612 11.4525 7.90451 11.4793 7.81648 11.5446C7.72859 11.61 7.67186 11.7081 7.66 11.8158C7.65042 11.9053 7.43035 14.0198 8.50742 15.3254C8.97844 15.8965 9.61821 16.2259 10.4096 16.304C10.5895 16.3223 10.7702 16.3317 10.9511 16.332C11.8846 16.332 12.6648 16.0702 13.275 15.5514C13.6736 15.205 13.9868 14.7741 14.1909 14.2914H15.068C15.2721 14.7741 15.5851 15.205 15.9835 15.5514C16.5941 16.0702 17.3739 16.332 18.3078 16.332H18.308C18.4887 16.3316 18.6694 16.3223 18.8493 16.304C19.6404 16.2254 20.2806 15.8964 20.7515 15.3254C21.8287 14.0199 21.6086 11.9055 21.5986 11.8158Z' fill='%23007AFF'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_4080_140366'%3E%3Crect width='29' height='29' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
                }
            }

            .message-bubble {
                max-width: 264px;

                .message-image {
                    display: flex;
                    justify-content: center;
                    margin-right: -16px;

                    &:has(.chat-message-pdf) {
                        margin-right: -16px;
                        margin-bottom: 0;
                    }

                    img {
                        object-fit: cover;
                        height: 256px;
                        width: 256px;
                    }
                }

                .message-text {
                    // & > span {
                    // display: inline-flex;
                    // flex-direction: column;
                    .read-status {
                        margin-left: 6px;
                        float: right;

                        .check-svg {
                            width: 16px;
                            height: 16px;
                            background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.6622 4.23019C13.3518 3.91634 12.846 3.91238 12.5308 4.22137L6.61746 10.0657L4.04688 7.40454C3.74029 7.085 3.23271 7.07451 2.9132 7.3811C2.59365 7.68769 2.58316 8.19527 2.88976 8.51478C2.89115 8.5162 2.8925 8.51763 2.89389 8.51905L6.02817 11.7672C6.17816 11.9202 6.38317 12.0068 6.59746 12.0077H6.60628C6.81649 12.0071 7.01807 11.9238 7.16754 11.776L13.6549 5.36154C13.9684 5.05071 13.9716 4.54501 13.6622 4.23019Z' fill='white'/%3E%3C/svg%3E%0A");

                            &.was-read {
                                background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_3219_105256)'%3E%3Cpath d='M10.9956 4.23019C10.6853 3.91634 10.1795 3.91238 9.8643 4.22137L3.95096 10.0657L1.38037 7.40454C1.07378 7.085 0.566209 7.07451 0.246696 7.3811C-0.072851 7.68769 -0.0833393 8.19527 0.223254 8.51478C0.224643 8.5162 0.225997 8.51763 0.227386 8.51905L3.36166 11.7672C3.51166 11.9202 3.71667 12.0068 3.93095 12.0077H3.93977C4.14999 12.0071 4.35156 11.9238 4.50104 11.776L10.9884 5.36154C11.3019 5.05071 11.3051 4.54501 10.9956 4.23019Z' fill='white'/%3E%3Cpath d='M8.1724 11.7672C8.32201 11.9199 8.52636 12.0064 8.74009 12.0078H8.74891C8.95913 12.0071 9.1607 11.9239 9.31018 11.776L15.7976 5.36159C16.0917 5.03048 16.0616 4.52371 15.7305 4.22965C15.4304 3.96307 14.9793 3.95956 14.6751 4.22142L8.76412 10.0658L8.55324 9.84689C8.24669 9.52734 7.73911 9.51682 7.41957 9.82341C7.10002 10.13 7.0895 10.6375 7.39609 10.9571C7.39748 10.9585 7.39887 10.96 7.40026 10.9614L8.1724 11.7672Z' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_3219_105256'%3E%3Crect width='16' height='16' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
                            }
                        }
                    }

                    // }
                }
            }
        }

        .message-received {
            .message-image {
                //margin-left: -16px;
                //padding: 4px;
                //img {
                //    border-bottom-right-radius: var(--f7-message-bubble-border-radius) !important;
                //    border-bottom-left-radius: var(--f7-message-bubble-border-radius) !important;
                //}
            }
        }

        .message-sent {
            .message-image {
                //margin-right: -16px;
                //padding: 4px;
                //img {
                //    border-bottom-right-radius: var(--f7-message-bubble-border-radius) !important;
                //    border-bottom-left-radius: var(--f7-message-bubble-border-radius) !important;
                //}
            }
        }
    }

    .preloader {
        width: 34px;
        height: 24px;
        margin-left: 8px;
    }

    .i-svg-arrow-top {
        cursor: pointer;
        margin-left: 8px;
        width: 34px;
        height: 34px;
        background-image: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 14C0 6.26801 6.26801 0 14 0V0C21.732 0 28 6.26801 28 14V14C28 21.732 21.732 28 14 28V28C6.26801 28 0 21.732 0 14V14Z' fill='%23818E95'/%3E%3Cpath d='M13.9962 21C14.5607 21 14.9498 20.6252 14.9498 20.0667V11.358L14.8811 9.76325L16.6891 11.7328L18.4132 13.379C18.5887 13.5407 18.8175 13.6509 19.0922 13.6509C19.6109 13.6509 20 13.2835 20 12.769C20 12.5265 19.9008 12.2987 19.7025 12.1076L14.7057 7.28661C14.5226 7.10289 14.2556 7 13.9962 7C13.7368 7 13.4774 7.10289 13.2943 7.28661L8.29752 12.1076C8.09917 12.2987 8 12.5265 8 12.769C8 13.2835 8.38907 13.6509 8.90782 13.6509C9.18245 13.6509 9.41132 13.5407 9.58678 13.379L11.3032 11.7328L13.1113 9.76325L13.0502 11.358V20.0667C13.0502 20.6252 13.4317 21 13.9962 21Z' fill='white'/%3E%3C/svg%3E%0A");

        &.active {
            background-image: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 14C0 6.26801 6.26801 0 14 0V0C21.732 0 28 6.26801 28 14V14C28 21.732 21.732 28 14 28V28C6.26801 28 0 21.732 0 14V14Z' fill='%23007AFF'/%3E%3Cpath d='M13.9962 21C14.5607 21 14.9498 20.6252 14.9498 20.0667V11.358L14.8811 9.76325L16.6891 11.7328L18.4132 13.379C18.5887 13.5407 18.8175 13.6509 19.0922 13.6509C19.6109 13.6509 20 13.2835 20 12.769C20 12.5265 19.9008 12.2987 19.7025 12.1076L14.7057 7.28661C14.5226 7.10289 14.2556 7 13.9962 7C13.7368 7 13.4774 7.10289 13.2943 7.28661L8.29752 12.1076C8.09917 12.2987 8 12.5265 8 12.769C8 13.2835 8.38907 13.6509 8.90782 13.6509C9.18245 13.6509 9.41132 13.5407 9.58678 13.379L11.3032 11.7328L13.1113 9.76325L13.0502 11.358V20.0667C13.0502 20.6252 13.4317 21 13.9962 21Z' fill='white'/%3E%3C/svg%3E%0A");
        }
    }


}

.i-svg-icon-close {
    width: 24px;
    height: 24px;
    filter: brightness(0) saturate(100%) invert(26%) sepia(93%) saturate(2553%) hue-rotate(203deg) brightness(107%) contrast(105%);
    background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M13.6159 12.0001L19.6649 5.95111C20.1117 5.50426 20.1117 4.78199 19.6649 4.33514C19.218 3.88829 18.4958 3.88829 18.0489 4.33514L12 10.3842L5.95107 4.33514C5.50423 3.88829 4.78197 3.88829 4.33513 4.33514C3.88829 4.78199 3.88829 5.50426 4.33513 5.95111L10.3841 12.0001L4.33513 18.0492C3.88829 18.496 3.88829 19.2183 4.33513 19.6651C4.55798 19.888 4.85054 20 5.1431 20C5.43566 20 5.72822 19.888 5.95107 19.6651L12 13.6161L18.0489 19.6651C18.2718 19.888 18.5643 20 18.8569 20C19.1495 20 19.442 19.888 19.6649 19.6651C20.1117 19.2183 20.1117 18.496 19.6649 18.0492L13.6159 12.0001Z' fill='white'/%3E%3C/svg%3E%0A");
}
</style>
